import type { CompanyType } from '@wtta/scoring'

interface SystemImpactSectionProps {
  companyType: CompanyType
  contactUrl: string
}

export function SystemImpactSection({
  companyType,
  contactUrl,
}: SystemImpactSectionProps) {
  return (
    <section
      className="report-section system-impact"
      data-testid="system-impact-section"
    >
      <h2 className="report-section__title">Wat dit betekent voor jouw systemen</h2>

      <p className="system-impact__lead">
        Veel van wat in dit rapport staat klinkt als juridisch werk, maar landt
        uiteindelijk in jouw operationele systemen.
      </p>

      {companyType === 'uitlener' ? (
        <>
          <p className="system-impact__paragraph">
            De inlenersbeloning aantoonbaar correct toepassen, vraagt om een
            CAO-engine die per detachering kan rekenen en die historiseert wat
            er bij elke uitbetaling gold. G-rekening-gebruik aantoonbaar maken,
            vraagt om een koppeling tussen jouw inkoop, betaling en boekhouding
            zodat je het bedrag per opdracht kunt herleiden. Een loonadministratie
            die per opdracht laat zien welke CAO is toegepast en welke toeslagen
            zijn meegenomen, is geen rapportage-feature meer. Het is bewijslast.
          </p>
          <p className="system-impact__paragraph">
            Dit is precies waar mid-office samenkomt: onboarding van uitleners
            en arbeidskrachten, contractbeheer met clausules en versies,
            CAO-toepassing en tarieven, urenregistratie en tijdinterpretatie.
            Wat in 2024 nog werkte met een spreadsheet en een paar handmatige
            checks, breekt zodra een toezichthouder een willekeurig dossier
            opvraagt en je niet binnen redelijke termijn kunt reproduceren wat
            er op een specifieke datum gold.
          </p>
        </>
      ) : (
        <>
          <p className="system-impact__paragraph">
            Een register van uitleners dat actueel is, vraagt om een proces dat
            herinneringen geeft, periodiek de toelatingsstatus checkt en de
            uitkomst vastlegt. Een onboarding voor nieuwe leveranciers waarin
            de toelating-check een verplichte stap is, vraagt om een inkoopflow
            die niet vooruit kan zonder dat vinkje. Een audit trail die per
            inhuur-opdracht laat zien dat er gecontroleerd is, vraagt om opslag
            op een plek waar het ook over twee jaar nog gevonden wordt.
          </p>
          <p className="system-impact__paragraph">
            Dit is precies waar mid-office samenkomt: onboarding,
            contractbeheer, tarieven, urenregistratie en tijdinterpretatie.
            Wat in 2024 nog werkte met een spreadsheet en een paar handmatige
            checks, breekt zodra een toezichthouder een willekeurig dossier
            opvraagt en je niet binnen redelijke termijn kunt reproduceren wat
            er op een specifieke datum gold.
          </p>
        </>
      )}

      <p className="system-impact__paragraph">
        De uitkomst hoeft niet meteen 'systeem migreren' te zijn. In de praktijk
        lopen drie sporen vaak naast elkaar: bestaande systemen aanvullen waar
        het kan, processen automatiseren rond de bewijslast en stapsgewijs
        vervangen wat onhoudbaar is geworden.
      </p>

      <div className="system-impact__cta" data-testid="system-impact-cta">
        <h3 className="system-impact__cta-title">Verder praten</h3>
        <p className="system-impact__cta-text">
          Wij denken graag mee als je tegen iets specifieks aanloopt. Bijvoorbeeld
          hoe je inlenersbeloning auditbaar inricht, hoe je uitleners-toetsing een
          vast onderdeel maakt van jouw inkoopproces, of waar AI-automation het
          handmatige werk uit jouw mid-office kan halen. Een gesprek van een uur
          is genoeg om te bepalen of er iets concreets aan te pakken is. Kies
          hieronder rechtstreeks een tijdslot in onze agenda.
        </p>
        <a
          className="system-impact__cta-link"
          href={contactUrl}
          rel="noopener noreferrer"
          target="_blank"
          data-testid="system-impact-cta-link"
        >
          Plan een gesprek
        </a>
      </div>
    </section>
  )
}
